// src/services/reward.service.ts

import api from "./api";

// Típusok
export interface Reward {
  _id: string;
  shopId: string;
  name: string;
  description?: string;
  pointsRequired: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Redemption {
  _id: string;
  shopId: string | { _id: string; name: string };
  rewardId: string | { _id: string; name: string; pointsRequired: number };
  userId: string | { _id: string; name: string; email?: string };
  employeeId?: string | { _id: string; name: string };
  pointsSpent: number;
  status: "pending" | "completed" | "cancelled";
  redeemedAt: string;
  createdAt: string;
}

export interface RewardQRData {
  type: "reward_redemption";
  userId: string;
  shopId: string;
  rewardId: string;
  rewardName: string;
  pointsRequired: number;
  timestamp: number;
  signature: string;
}

export interface ManualPointsRequest {
  email: string;
  points: number;
  reason?: string;
}

export interface ManualPointsResponse {
  msg: string;
  user: {
    id: string;
    name: string;
    email: string;
  };
  pointsAdded: number;
  currentPoints: number;
  totalPointsEarned: number;
  reason?: string;
}

class RewardService {
  // ============================================
  // JUTALMAK KEZELÉSE
  // ============================================

  /**
   * Bolt jutalmainak lekérdezése
   */
  async getShopRewards(
    shopId: string,
    activeOnly: boolean = false
  ): Promise<Reward[]> {
    try {
      const response = await api.get(`/api/rewards/shop/${shopId}`, {
        params: activeOnly ? { activeOnly: "true" } : {},
      });
      return response.data.rewards || [];
    } catch (error: any) {
      console.error(
        "Hiba a jutalmak lekérdezésekor:",
        error.response?.data || error.message
      );
      throw error;
    }
  }

  /**
   * Egy jutalom lekérdezése
   */
  async getRewardById(rewardId: string): Promise<Reward> {
    try {
      const response = await api.get(`/api/rewards/${rewardId}`);
      return response.data.reward;
    } catch (error: any) {
      console.error(
        "Hiba a jutalom lekérdezésekor:",
        error.response?.data || error.message
      );
      throw error;
    }
  }

  /**
   * Új jutalom létrehozása
   */
  async createReward(
    shopId: string,
    data: {
      name: string;
      description?: string;
      pointsRequired: number;
      isActive?: boolean;
    }
  ): Promise<Reward> {
    try {
      const response = await api.post(`/api/rewards/shop/${shopId}`, data);
      return response.data.reward;
    } catch (error: any) {
      console.error(
        "Hiba a jutalom létrehozásakor:",
        error.response?.data || error.message
      );
      throw error;
    }
  }

  /**
   * Jutalom módosítása
   */
  async updateReward(
    rewardId: string,
    data: {
      name?: string;
      description?: string;
      pointsRequired?: number;
      isActive?: boolean;
    }
  ): Promise<Reward> {
    try {
      const response = await api.put(`/api/rewards/${rewardId}`, data);
      return response.data.reward;
    } catch (error: any) {
      console.error(
        "Hiba a jutalom módosításakor:",
        error.response?.data || error.message
      );
      throw error;
    }
  }

  /**
   * Jutalom törlése
   */
  async deleteReward(rewardId: string): Promise<void> {
    try {
      await api.delete(`/api/rewards/${rewardId}`);
    } catch (error: any) {
      console.error(
        "Hiba a jutalom törlésekor:",
        error.response?.data || error.message
      );
      throw error;
    }
  }

  // ============================================
  // JUTALOM BEVÁLTÁS
  // ============================================

  /**
   * Jutalom QR kód generálása (vásárló)
   */
  async generateRewardQR(rewardId: string): Promise<RewardQRData> {
    try {
      const response = await api.post(`/api/rewards/${rewardId}/generate-qr`);
      return response.data.qrData;
    } catch (error: any) {
      console.error(
        "Hiba a QR kód generálásakor:",
        error.response?.data || error.message
      );
      throw error;
    }
  }

  /**
   * Jutalom beváltása QR alapján (bolt)
   */
  async redeemReward(qrData: RewardQRData): Promise<{
    msg: string;
    redemption: Redemption;
    user: { id: string; name: string; email?: string };
    reward: { id: string; name: string; pointsSpent: number };
    remainingPoints: number;
  }> {
    try {
      const response = await api.post("/api/rewards/redeem", qrData);
      return response.data;
    } catch (error: any) {
      console.error(
        "Hiba a jutalom beváltásakor:",
        error.response?.data || error.message
      );
      throw error;
    }
  }

  // ============================================
  // BEVÁLTÁSOK LEKÉRDEZÉSE
  // ============================================

  /**
   * Bolt beváltásainak lekérdezése
   */
  async getShopRedemptions(
    shopId: string,
    page: number = 1,
    limit: number = 20
  ): Promise<{
    redemptions: Redemption[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }> {
    try {
      const response = await api.get(
        `/api/rewards/shop/${shopId}/redemptions`,
        {
          params: { page, limit },
        }
      );
      return response.data;
    } catch (error: any) {
      console.error(
        "Hiba a beváltások lekérdezésekor:",
        error.response?.data || error.message
      );
      throw error;
    }
  }

  /**
   * Felhasználó beváltásainak lekérdezése
   */
  async getUserRedemptions(
    userId: string,
    page: number = 1,
    limit: number = 20
  ): Promise<{
    redemptions: Redemption[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }> {
    try {
      const response = await api.get(
        `/api/rewards/user/${userId}/redemptions`,
        {
          params: { page, limit },
        }
      );
      return response.data;
    } catch (error: any) {
      console.error(
        "Hiba a beváltások lekérdezésekor:",
        error.response?.data || error.message
      );
      throw error;
    }
  }

  // ============================================
  // MANUÁLIS PONT JÓVÁÍRÁS
  // ============================================

  /**
   * Manuális pont jóváírás email alapján
   */
  async addManualPoints(
    shopId: string,
    data: ManualPointsRequest
  ): Promise<ManualPointsResponse> {
    try {
      const response = await api.post(
        `/api/rewards/shop/${shopId}/manual-points`,
        data
      );
      return response.data;
    } catch (error: any) {
      console.error(
        "Hiba a pont jóváíráskor:",
        error.response?.data || error.message
      );
      throw error;
    }
  }
}

const rewardService = new RewardService();
export default rewardService;
